<template>
  <a-modal
      v-model:visible="visible"
      :title="`${isEditMode ? '编辑' : '新增'}工单子表信息`"
      :on-before-ok="handleOk"
      @cancel="handleCancel"
      width="90%"
      :mask-closable="false"
      :ok-loading="loading"
      draggable
  >
    <a-tabs v-model:activeKey="activeTab" type="card">
      <!-- 签到记录 -->
      <a-tab-pane key="checkin" title="签到记录">
        <a-card>
          <a-button type="primary" @click="addCheckinRecord" style="margin-bottom: 16px;">
            <icon-plus/>
            添加签到记录
          </a-button>
          <a-table
              row-key="id"
              :columns="checkinRecordColumns"
              :data="checkinRecords"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
              :row-class="getRowClass"
          >
            <template #checkinTime="{ record }">
              <a-date-picker
                  v-model="record.checkinTime"
                  format="YYYY-MM-DD HH:mm:ss"
                  show-time
                  :disabled="record._toDelete"
              />
            </template>
            <template #checkinLongitude="{ record }">
              <a-input-number
                  v-model="record.checkinLongitude"
                  placeholder="签到经度"
                  style="width: 100%"
                  :precision="7"
                  :disabled="record._toDelete"
              />
            </template>
            <template #checkinLatitude="{ record }">
              <a-input-number
                  v-model="record.checkinLatitude"
                  placeholder="签到纬度"
                  style="width: 100%"
                  :precision="7"
                  :disabled="record._toDelete"
              />
            </template>
            <template #checkinAddress="{ record }">
              <!-- 隐藏签到地址编辑功能，仅显示文本 -->
              <span>{{ record.checkinAddress || '—' }}</span>
            </template>
            <template #invalidStatus="{ record }">
              <a-select
                  v-model="record.invalidStatus"
                  :options="[
                    { label: '未校验', value: 0 },
                    { label: '正常', value: 1 },
                    { label: '异常', value: 2 }
                  ]"
                  placeholder="请选择校验状态"
                  :disabled="record._toDelete"
              />
            </template>
            <template #workStartTime="{ record }">
              <a-date-picker
                  v-model="record.workStartTime"
                  format="YYYY-MM-DD HH:mm:ss"
                  show-time
                  :disabled="record._toDelete"
              />
            </template>
            <template #workSubmitTime="{ record }">
              <a-date-picker
                  v-model="record.workSubmitTime"
                  format="YYYY-MM-DD HH:mm:ss"
                  show-time
                  :disabled="record._toDelete"
              />
            </template>
            <template #actions="{ record }">
              <div v-if="record._toDelete" style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="success" @click="restoreCheckinRecord(record)">
                  恢复
                </a-button>
              </div>
              <div v-else style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="danger" @click="deleteCheckinRecord(record)">
                  删除
                </a-button>
              </div>
            </template>
          </a-table>
        </a-card>
      </a-tab-pane>

      <!-- 行动项目 -->
      <a-tab-pane key="action" title="行动项目">
        <a-card>
          <a-button type="primary" @click="addActionItem" style="margin-bottom: 16px;">
            <icon-plus/>
            添加行动项目
          </a-button>
          <a-table
              row-key="id"
              :columns="actionItemColumns"
              :data="actionItems"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
              :row-class="getRowClass"
          >
            <template #caseTypeName="{ record }">
              <a-select
                  v-model="record.caseTypeId"
                  :options="caseTypeOptions"
                  :field-names="{ value: 'id', label: 'name' }"
                  placeholder="请选择情况类型"
                  allow-clear
                  :disabled="record._toDelete"
              />
            </template>
            <template #regionName="{ record }">
              <div style="display: flex; gap: 8px;">
                <a-cascader :options="cascaderRegionOptions"
                            v-model="record.regionId" placeholder="请选择区域"
                            @change="handleRegionChange(record, value, option)"
                            :disabled="record._toDelete"/>
                <a-textarea
                    v-model="record.regionText"
                    placeholder="区域详情"
                    style="flex: 1; min-width: 120px;"
                    :disabled="record._toDelete"
                />
              </div>
            </template>
            <template #caseDescriptionText="{ record }">
              <div style="display: flex; flex-direction: column; gap: 4px; min-width: 200px;">
                <a-select
                    v-model="record.caseDescriptionIdsArray"
                    :options="caseDescriptionOptions"
                    :field-names="{ value: 'id', label: 'name' }"
                    placeholder="请选择风险类型"
                    allow-clear
                    multiple
                    @change="(value, option) => handleCaseDescriptionChange(record, value, option)"
                    :disabled="record._toDelete"
                />
                <a-textarea
                    v-model="record.caseDescriptionText"
                    placeholder="请输入风险描述"
                    :disabled="record._toDelete"
                />
              </div>
            </template>
            <template #suggestionMeasure="{ record }">
              <div style="display: flex; flex-direction: column; gap: 4px; min-width: 200px;">
                <a-select
                    v-model="record.suggestionMeasureId"
                    :options="suggestionMeasureOptions"
                    :field-names="{ value: 'id', label: 'name' }"
                    placeholder="请选择建议措施"
                    allow-clear
                    @change="(value, option) => handleSuggestionMeasureChange(record, value, option)"
                    :disabled="record._toDelete"
                />
                <a-textarea
                    v-model="record.suggestionMeasureText"
                    placeholder="请输入措施补充说明"
                    :disabled="record._toDelete"
                />
              </div>
            </template>
            <template #followerName="{ record }">
              <a-textarea
                  v-model="record.followerName"
                  placeholder="跟进方"
                  :disabled="record._toDelete"
              />
            </template>
            <template #followUpCount="{ record }">
              <a-input-number
                  v-model="record.followUpCount"
                  placeholder="跟进次数"
                  style="width: 100%"
                  :min="0"
                  :disabled="record._toDelete"
              />
            </template>
            <template #completed="{ record }">
              <a-switch v-model="record.completed" :disabled="record._toDelete"/>
            </template>
            <template #actionItemRemark="{ record }">
              <a-textarea
                  v-model="record.remark"
                  placeholder="请输入备注"
                  :disabled="record._toDelete"
              />
            </template>
            <template #photoRecords="{ record }">
              <a-upload
                  :custom-request="(options) => handlePhotoUpload(options, record, 'photoRecords')"
                  :file-list="getPhotoFileList(record, 'photoRecords')"
                  multiple
                  list-type="picture-card"
                  image-preview
                  draggable
                  @change="(fileList) => handlePhotoChange(fileList, record, 'photoRecords')"
                  :disabled="record._toDelete"
              >
                <template #upload-button>
                  <div class="upload-button-content">
                    <icon-plus/>
                    <div style="margin-top: 10px;">上传照片</div>
                  </div>
                </template>
              </a-upload>
            </template>
            <template #actions="{ record }">
              <div v-if="record._toDelete" style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="success" @click="restoreActionItem(record)">
                  恢复
                </a-button>
              </div>
              <div v-else style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="danger" @click="deleteActionItem(record)">
                  删除
                </a-button>
              </div>
            </template>
          </a-table>
        </a-card>
      </a-tab-pane>

      <!-- 虫情勘察 -->
      <a-tab-pane key="pest" title="虫情勘察">
        <a-card>
          <a-button type="primary" @click="addPestSurvey" style="margin-bottom: 16px;">
            <icon-plus/>
            添加虫情勘察
          </a-button>
          <a-table
              row-key="id"
              :columns="pestSurveyColumns"
              :data="pestSurveys"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
              :row-class="getRowClass"
          >
            <template #regionName="{ record }">
              <div style="display: flex; gap: 8px;">
                <a-cascader :options="cascaderRegionOptions"
                            v-model="record.regionId" placeholder="请选择区域"
                            @change="handleRegionChange(record, value, option)"
                            :disabled="record._toDelete"/>
              </div>
            </template>
            <template #mouseCount="{ record }">
              <a-input-number
                  v-model="record.mouseCount"
                  placeholder="鼠类数量"
                  style="width: 100%"
                  :min="0"
                  :disabled="record._toDelete"
              />
            </template>
            <template #mouseDetails="{ record }">
              <a-textarea
                  v-model="record.mouseDetails"
                  placeholder="鼠类详情"
                  :disabled="record._toDelete"
              />
            </template>
            <template #hasMouseTrace="{ record }">
              <a-switch v-model="record.hasMouseTrace" :disabled="record._toDelete"/>
            </template>
            <template #cockroachCount="{ record }">
              <a-input-number
                  v-model="record.cockroachCount"
                  placeholder="蟑螂数量"
                  style="width: 100%"
                  :min="0"
                  :disabled="record._toDelete"
              />
            </template>
            <template #cockroachDetails="{ record }">
              <a-textarea
                  v-model="record.cockroachDetails"
                  placeholder="蟑螂详情"
                  :disabled="record._toDelete"
              />
            </template>
            <template #flyingInsectCount="{ record }">
              <a-input-number
                  v-model="record.flyingInsectCount"
                  placeholder="飞虫数量"
                  style="width: 100%"
                  :min="0"
                  :disabled="record._toDelete"
              />
            </template>
            <template #remark="{ record }">
              <a-textarea
                  v-model="record.remark"
                  placeholder="备注"
                  :disabled="record._toDelete"
              />
            </template>
            <template #actions="{ record }">
              <div v-if="record._toDelete" style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="success" @click="restorePestSurvey(record)">
                  恢复
                </a-button>
              </div>
              <div v-else style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="danger" @click="deletePestSurvey(record)">
                  删除
                </a-button>
              </div>
            </template>
          </a-table>
        </a-card>
      </a-tab-pane>

      <!-- 设备详情 -->
      <a-tab-pane key="device" title="设备详情">
        <a-card>
          <a-button type="primary" @click="addDeviceDetail" style="margin-bottom: 16px;">
            <icon-plus/>
            添加设备详情
          </a-button>
          <a-table
              row-key="id"
              :columns="deviceDetailsColumns"
              :data="deviceDetails"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
              :row-class="getRowClass"
          >
            <template #regionName="{ record }">
              <div style="display: flex; gap: 8px;">
                <a-cascader :options="cascaderRegionOptions"
                            v-model="record.regionId" placeholder="请选择区域"
                            @change="handleRegionChange(record, value, option)"
                            :disabled="record._toDelete"/>
              </div>
            </template>
            <template #deviceLocationName="{ record }">
              <a-select
                  v-model="record.deviceLocationId"
                  :options="deviceLocationOptions"
                  :field-names="{ value: 'id', label: 'name' }"
                  placeholder="请选择设备位置"
                  @change="(value) => handleDeviceLocationChange(record, value)"
                  allow-clear
                  :disabled="record._toDelete"
              />
            </template>
            <template #deviceLocationText="{ record }">
              <a-textarea
                  v-model="record.deviceLocationText"
                  placeholder="备注"
                  :disabled="record._toDelete"
              />
            </template>
            <template #deviceTypeName="{ record }">
              <a-select
                  v-model="record.deviceTypeId"
                  :options="deviceTypeOptions"
                  :field-names="{ value: 'id', label: 'name' }"
                  placeholder="请选择设备类型"
                  @change="(value) => handleDeviceTypeChange(record, value)"
                  allow-clear
                  :disabled="record._toDelete"
              />
            </template>
            <template #deviceConditionName="{ record }">
              <a-select
                  v-model="record.deviceConditionId"
                  :options="deviceConditionOptions"
                  :field-names="{ value: 'id', label: 'name' }"
                  @change="(value) => handleDeviceConditionChange(record, value)"
                  placeholder="请选择设备状况"
                  allow-clear
                  :disabled="record._toDelete"
              />
            </template>
            <template #materialReplaced="{ record }">
              <a-switch v-model="record.materialReplaced" :disabled="record._toDelete"/>
            </template>
            <template #captured="{ record }">
              <a-switch v-model="record.captured" :disabled="record._toDelete"/>
            </template>
            <template #captureCount="{ record }">
              <a-input-number
                  v-model="record.captureCount"
                  placeholder="捕获数量"
                  style="width: 100%"
                  :min="0"
                  :disabled="record._toDelete"
              />
            </template>
            <template #remark="{ record }">
              <a-textarea
                  v-model="record.remark"
                  placeholder="备注"
                  :disabled="record._toDelete"
              />
            </template>
            <template #photoRecords="{ record }">
              <a-upload
                  :custom-request="(options) => handlePhotoUpload(options, record, 'photoRecords')"
                  :file-list="getPhotoFileList(record, 'photoRecords')"
                  multiple
                  list-type="picture-card"
                  image-preview
                  draggable
                  @change="(fileList) => handlePhotoChange(fileList, record, 'photoRecords')"
                  :disabled="record._toDelete"
              >
                <template #upload-button>
                  <div class="upload-button-content">
                    <icon-plus/>
                    <div style="margin-top: 10px;">上传照片</div>
                  </div>
                </template>
              </a-upload>
            </template>
            <template #actions="{ record }">
              <div v-if="record._toDelete" style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="success" @click="restoreDeviceDetail(record)">
                  恢复
                </a-button>
              </div>
              <div v-else style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="danger" @click="deleteDeviceDetail(record)">
                  删除
                </a-button>
              </div>
            </template>
          </a-table>
        </a-card>
      </a-tab-pane>

      <!-- 物料汇总 -->
      <a-tab-pane key="material" title="物料汇总">
        <a-card>
          <a-button type="primary" @click="addMaterialSummary" style="margin-bottom: 16px;">
            <icon-plus/>
            添加物料汇总
          </a-button>
          <a-table
              row-key="id"
              :columns="materialSummaryColumns"
              :data="materialSummaries"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
              :row-class="getRowClass"
          >
            <template #pestTypeName="{ record }">
              <div style="display: flex; gap: 8px;">
                <a-select
                    v-model="record.pestTypeId"
                    :options="pestTypeOptions"
                    :field-names="{ value: 'id', label: 'name' }"
                    placeholder="请选择标靶虫害"
                    allow-clear
                    style="flex: 1; min-width: 120px;"
                    @change="(value) => handlePestTypeChange(record, value)"
                    :disabled="record._toDelete"
                />
              </div>
            </template>
            <template #materialName="{ record }">
              <a-select
                  v-model="record.materialId"
                  :options="filterMaterialOptions(record.pestTypeId)"
                  :field-names="{ value: 'id', label: 'name' }"
                  placeholder="请选择物料"
                  allow-clear
                  @change="(value) => handleMaterialChange(record, value)"
                  :disabled="record._toDelete"
              />
            </template>
            <template #dilutionRatio="{ record }">
              <a-textarea
                  v-model="record.dilutionRatio"
                  placeholder="稀释比例"
                  readonly
                  :disabled="record._toDelete"
              />
            </template>
            <template #materialQuantity="{ record }">
              <a-input-number
                  v-model="record.materialQuantity"
                  placeholder="使用数量"
                  style="width: 100%"
                  :min="0"
                  :precision="2"
                  :disabled="record._toDelete"
              />
            </template>
            <template #unit="{ record }">
              <a-textarea
                  v-model="record.unit"
                  placeholder="计量单位"
                  readonly
                  :disabled="record._toDelete"
              />
            </template>
            <template #usageMethod="{ record }">
              <a-textarea
                  v-model="record.usageMethod"
                  placeholder="使用方法"
                  readonly
                  :disabled="record._toDelete"
              />
            </template>
            <template #remark="{ record }">
              <a-textarea
                  v-model="record.remark"
                  placeholder="备注"
                  :disabled="record._toDelete"
              />
            </template>
            <template #actions="{ record }">
              <div v-if="record._toDelete" style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="success" @click="restoreMaterialSummary(record)">
                  恢复
                </a-button>
              </div>
              <div v-else style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="danger" @click="deleteMaterialSummary(record)">
                  删除
                </a-button>
              </div>
            </template>
          </a-table>
        </a-card>
      </a-tab-pane>

      <!-- 集团定制 -->
      <a-tab-pane key="groupCustom" title="集团定制">
        <a-card>
          <a-button type="primary" @click="addGroupCustomItem" style="margin-bottom: 16px;">
            <icon-plus/>
            添加定制项
          </a-button>
          <a-table
              row-key="id"
              :columns="groupCustomColumns"
              :data="groupCustomItems"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
              :row-class="getRowClass"
          >
            <template #storeCustomConfigId="{ record }">
              <a-select
                  v-model="record.storeCustomConfigId"
                  :options="storeCustomConfigOptions"
                  :field-names="{ value: 'storeCustomConfigId', label: 'optionLabel' }"
                  placeholder="请选择定制配置"
                  allow-clear
                  :disabled="record._toDelete"
                  @change="(value, option) => handleStoreCustomConfigChange(record, value,option)"
              />
            </template>
            <template #customRemark="{ record }">
              <a-textarea
                  v-model="record.remark"
                  autoSize
                  :disabled="true"
              />
            </template>
            <template #optionType="{ record }">
              <a-input
                  v-model="record.optionType"
                  placeholder="字段类型"
                  :disabled="true"
              />
            </template>
            <template #storeCustomValue="{ record }">
              <template v-if="record.optionType === '是否'">
                <a-switch
                    v-model="record.storeCustomValue"
                    :disabled="record._toDelete"
                />
              </template>
              <template v-else-if="record.optionType === '选项'">
                <a-select
                    v-model="record.storeCustomValue"
                    :disabled="record._toDelete"
                >
                  <a-option v-for="option in record.optionValues.split(',')" :key="option">
                    {{ option }}
                  </a-option>
                </a-select>
              </template>
              <template v-else-if="record.optionType === '图片'">
                <a-upload
                    :custom-request="(options) => handlePhotoUploadForCustomField(options, record)"
                    :file-list="getPhotoFileListForCustomField(record)"
                    multiple
                    list-type="picture-card"
                    image-preview
                    draggable
                    @change="(fileList) => handlePhotoChangeForCustomField(fileList, record)"
                    :disabled="record._toDelete"
                >
                  <template #upload-button>
                    <div class="upload-button-content">
                      <icon-plus/>
                      <div style="margin-top: 10px;">上传照片</div>
                    </div>
                  </template>
                </a-upload>
              </template>
              <template v-else-if="record.optionType === '数字'">
                <a-input-number
                    v-model="record.storeCustomValue"
                    placeholder="请输入数字"
                    style="width: 100%"
                    :disabled="record._toDelete"
                />
              </template>
              <template v-else>
                <a-textarea
                    v-model="record.storeCustomValue"
                    placeholder="请输入值"
                    :disabled="record._toDelete"
                />
              </template>
            </template>
            <template #isRequired="{ record }">
              <a-switch
                  v-model="record.isRequired"
                  :disabled="true"
              />
            </template>
            <template #actions="{ record }">
              <div v-if="record._toDelete" style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="success" @click="restoreGroupCustomItem(record)">
                  恢复
                </a-button>
              </div>
              <div v-else style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="danger" @click="deleteGroupCustomItem(record)">
                  删除
                </a-button>
              </div>
            </template>
          </a-table>
        </a-card>
      </a-tab-pane>

      <!-- 签字记录 -->
      <a-tab-pane key="signature" title="签字记录">
        <a-card>
          <a-button type="primary" @click="addSignatureRecord" style="margin-bottom: 16px;">
            <icon-plus/>
            添加签字记录
          </a-button>
          <a-table
              row-key="id"
              :columns="signatureRecordColumns"
              :data="signatureRecords"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
              :row-class="getRowClass"
          >
            <template #signatureTime="{ record }">
              <a-date-picker
                  v-model="record.signatureTime"
                  format="YYYY-MM-DD HH:mm:ss"
                  show-time
                  :disabled="record._toDelete"
              />
            </template>
            <template #signerType="{ record }">
              <a-select
                  v-model="record.signerType"
                  :options="[
                    { label: '技术人员', value: 'employee' },
                    { label: '客户', value: 'customer' }
                  ]"
                  placeholder="请选择签字人类型"
                  :disabled="record._toDelete"
              />
            </template>
            <template #signerName="{ record }">
              <a-select v-if="record.signerType === 'employee'"
                  v-model="record.signerName"
                  :options="userOptions"
                  :field-names="{ value: 'userName', label: 'userName' }"
                  placeholder="请选择签字人"
                  allow-clear
                  :disabled="record._toDelete"
              />
            </template>
            <template #signatureContent="{ record }">
<!--              <a-upload
                  :custom-request="(options) => handleSignatureUpload(options, record)"
                  :file-list="getSignatureFileList(record)"
                  :multiple="false"
                  list-type="picture-card"
                  image-preview
                  draggable
                  @change="(fileList) => handleSignatureChange(fileList, record)"
                  :disabled="record._toDelete"
              >
                <template #upload-button>
                  <div class="upload-button-content">
                    <icon-plus/>
                    <div style="margin-top: 10px;">上传签字</div>
                  </div>
                </template>
              </a-upload>-->
                <a-upload
                    :custom-request="(options) => handlePhotoUpload(options, record, 'signatureContent')"
                    :file-list="getPhotoFileList(record, 'signatureContent')"
                    multiple
                    list-type="picture-card"
                    image-preview
                    draggable
                    @change="(fileList) => handlePhotoChange(fileList, record, 'signatureContent')"
                    :disabled="record._toDelete"
                >
                  <template #upload-button>
                    <div class="upload-button-content">
                      <icon-plus/>
                      <div style="margin-top: 10px;">上传照片</div>
                    </div>
                  </template>
                </a-upload>
            </template>
            <template #signatureEvaluation="{ record }">
              <a-rate v-if="record.signerType==='customer'"
                  v-model="record.signatureEvaluation"
                  :count="5"
                  :disabled="record._toDelete"
              />
            </template>
            <template #evaluationDetail="{ record }">
              <a-textarea v-if="record.signerType==='customer'"
                  v-model="record.evaluationDetail"
                  placeholder="请输入评价详情"
                  :disabled="record._toDelete"
              />
            </template>
            <template #actions="{ record }">
              <div v-if="record._toDelete" style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="success" @click="restoreSignatureRecord(record)">
                  恢复
                </a-button>
              </div>
              <div v-else style="display: flex; flex-direction: column; gap: 4px;">
                <a-button type="text" size="mini" status="danger" @click="deleteSignatureRecord(record)">
                  删除
                </a-button>
              </div>
            </template>
          </a-table>
        </a-card>
      </a-tab-pane>
    </a-tabs>
  </a-modal>

</template>

<script setup>
import {ref, watch, onMounted} from 'vue'
import {Message} from '@arco-design/web-vue'
import {nanoid} from 'nanoid'
import {
  getWorkOrderInfo,
  getWorkOrderActionItemList,
  getWorkOrderPestSurveyList,
  getWorkOrderDeviceDetailList,
  getWorkOrderMaterialSummaryList,
  getWorkOrderCheckinRecordList,
  getWorkOrderExtensionInfoList,
  getWorkOrderSignatureRecordList,
  getStoreCustomConfigList,
  addWorkOrderActionItem,
  updateWorkOrderActionItem,
  deleteWorkOrderActionItem,
  addWorkOrderPestSurvey,
  updateWorkOrderPestSurvey,
  deleteWorkOrderPestSurvey,
  addWorkOrderDeviceDetail,
  updateWorkOrderDeviceDetail,
  deleteWorkOrderDeviceDetail,
  addWorkOrderMaterialSummary,
  updateWorkOrderMaterialSummary,
  deleteWorkOrderMaterialSummary,
  addWorkOrderCheckinRecord,
  updateWorkOrderCheckinRecord,
  deleteWorkOrderCheckinRecord,
  addWorkOrderExtensionInfo,
  updateWorkOrderExtensionInfo,
  deleteWorkOrderExtensionInfo,
  addWorkOrderSignatureRecord,
  updateWorkOrderSignatureRecord,
  deleteWorkOrderSignatureRecord,
  uploadFile
} from '@/apis/work-order-api'
import { getAllUser } from '@/apis/UserApi'
import {getInitBaseData} from '@/apis/base-data-api'
import {useUserStore} from "@/store/user";

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  workOrderId: {
    type: [String, Number],
    default: null
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue', 'success'])
const userStore = useUserStore(); // 在拦截器中调用
// 响应式数据
const visible = ref(false)
const loading = ref(false)
const activeTab = ref('action')
const isEditMode = ref(false)

// 子表数据（移除了签到记录）
const actionItems = ref([])
const pestSurveys = ref([])
const deviceDetails = ref([])
const materialSummaries = ref([])
const checkinRecords = ref([]) // 添加签到记录的定义
const groupCustomItems = ref([]) // 添加集团定制项的定义
const signatureRecords = ref([]) // 添加签字记录的定义

// 情况类型选项
const caseTypeOptions = ref([])
const regionOptions = ref([])
const caseDescriptionOptions = ref([])
const caseDescriptionMap = ref(new Map())
const suggestionMeasureOptions = ref([])
const storeTypeOptions = ref([])
const filteredRegionOptions = ref([]) // 用于存储根据门店类型筛选后的区域选项
const cascaderRegionOptions = ref([])
// 设备详情选项
const deviceLocationOptions = ref([])
const deviceTypeOptions = ref([])
const deviceConditionOptions = ref([])

// 物料汇总选项
const pestTypeOptions = ref([])
const materialOptions = ref([])

// 集团定制选项
const storeCustomConfigOptions = ref([])

// 用户选项
const userOptions = ref([])

// 行动项目列定义
const actionItemColumns = ref([
  {title: '情况类型', dataIndex: 'caseTypeName', width: 180, slotName: 'caseTypeName'},
  {title: '区域', dataIndex: 'regionName', width: 120, slotName: 'regionName'},
  {title: '风险类型', dataIndex: 'caseDescriptionText', width: 250, slotName: 'caseDescriptionText'},
  {title: '建议措施', dataIndex: 'suggestionMeasure', width: 250, slotName: 'suggestionMeasure'},
  {title: '跟进方', dataIndex: 'followerName', width: 120, slotName: 'followerName'},
  {title: '跟进次数', dataIndex: 'followUpCount', width: 120, slotName: 'followUpCount'},
  {title: '是否完成', dataIndex: 'completed', width: 120, slotName: 'completed'},
  {title: '备注', dataIndex: 'remark', width: 200, slotName: 'actionItemRemark'},
  {title: '照片', dataIndex: 'photoRecords', width: 150, slotName: 'photoRecords'},
  {title: '操作', dataIndex: 'actions', width: 100, slotName: 'actions'}
])

// 签到记录列定义
const checkinRecordColumns = ref([
  {title: '签到时间', dataIndex: 'checkinTime', width: 180, slotName: 'checkinTime'},
  {title: '签到经度', dataIndex: 'checkinLongitude', width: 120, slotName: 'checkinLongitude'},
  {title: '签到纬度', dataIndex: 'checkinLatitude', width: 120, slotName: 'checkinLatitude'},
  {title: '校验状态', dataIndex: 'invalidStatus', width: 120, slotName: 'invalidStatus'},
  {title: '开始作业时间', dataIndex: 'workStartTime', width: 180, slotName: 'workStartTime'},
  {title: '作业提交时间', dataIndex: 'workSubmitTime', width: 180, slotName: 'workSubmitTime'},
  {title: '操作', dataIndex: 'actions', width: 100, slotName: 'actions'}
])

// 虫情勘察列定义
const pestSurveyColumns = ref([
  {title: '勘察区域', dataIndex: 'regionName', width: 120, slotName: 'regionName'},
  {title: '鼠类数量', dataIndex: 'mouseCount', width: 120, slotName: 'mouseCount'},
  {title: '是否有鼠迹', dataIndex: 'hasMouseTrace', width: 120, slotName: 'hasMouseTrace'},
  {title: '鼠类详情', dataIndex: 'mouseDetails', width: 200, slotName: 'mouseDetails'},
  {title: '蟑螂数量', dataIndex: 'cockroachCount', width: 120, slotName: 'cockroachCount'},
  {title: '蟑螂详情', dataIndex: 'cockroachDetails', width: 200, slotName: 'cockroachDetails'},
  {title: '飞虫数量', dataIndex: 'flyingInsectCount', width: 120, slotName: 'flyingInsectCount'},
  {title: '备注', dataIndex: 'remark', width: 200, slotName: 'remark'},
  {title: '操作', dataIndex: 'actions', width: 100, slotName: 'actions'}
])
// 设备详情列定义
const deviceDetailsColumns = ref([
  {title: '区域', dataIndex: 'regionName', width: 120, slotName: 'regionName'},
  {title: '设备位置', dataIndex: 'deviceLocationName', width: 120, slotName: 'deviceLocationName'},
  {title: '位置描述', dataIndex: 'deviceLocationText', width: 120, slotName: 'deviceLocationText'},
  {title: '设备类型', dataIndex: 'deviceTypeName', width: 200, slotName: 'deviceTypeName'},
  {title: '设备状况', dataIndex: 'deviceConditionName', width: 120, slotName: 'deviceConditionName'},
  {title: '耗材更换', dataIndex: 'materialReplaced', width: 200, slotName: 'materialReplaced'},
  {title: '是否捕获', dataIndex: 'captured', width: 120, slotName: 'captured'},
  {title: '捕获数量', dataIndex: 'captureCount', width: 200, slotName: 'captureCount'},
  {title: '备注', dataIndex: 'remark', width: 200, slotName: 'remark'},
  {title: '照片', dataIndex: 'photoRecords', width: 150, slotName: 'photoRecords'},
  {title: '操作', dataIndex: 'actions', width: 100, slotName: 'actions'}
])


// 物料汇总列定义
const materialSummaryColumns = ref([
  {title: '标靶虫害', dataIndex: 'pestTypeName', width: 200, slotName: 'pestTypeName'},
  {title: '物料名称', dataIndex: 'materialName', width: 120, slotName: 'materialName'},
  {title: '稀释比例', dataIndex: 'dilutionRatio', width: 200, slotName: 'dilutionRatio'},
  {title: '施用量', dataIndex: 'materialQuantity', width: 120, slotName: 'materialQuantity'},
  {title: '计量单位', dataIndex: 'unit', width: 120, slotName: 'unit'},
  {title: '使用方法', dataIndex: 'usageMethod', width: 200, slotName: 'usageMethod'},
  {title: '备注', dataIndex: 'remark', width: 200, slotName: 'remark'},
  {title: '操作', dataIndex: 'actions', width: 100, slotName: 'actions'}
])

// 集团定制列定义
const groupCustomColumns = ref([
  {title: '选项标签', dataIndex: 'storeCustomConfigId', width: 80, slotName: 'storeCustomConfigId'},
  {title: '选项名称', dataIndex: 'remark', width: 500, slotName: 'customRemark'},
  {title: '字段类型', dataIndex: 'optionType', width: 80, slotName: 'optionType'},
  {title: '字段值', dataIndex: 'storeCustomValue', width: 80, slotName: 'storeCustomValue'},
  // {title: '是否必填', dataIndex: 'isRequired', width: 80, slotName: 'isRequired'},
  {title: '操作', dataIndex: 'actions', width: 80, slotName: 'actions'}
])

// 签字记录列定义
const signatureRecordColumns = ref([
  {title: '签字时间', dataIndex: 'signatureTime', width: 180, slotName: 'signatureTime'},
  {title: '签字人类型', dataIndex: 'signerType', width: 120, slotName: 'signerType'},
  {title: '签字人', dataIndex: 'signerName', width: 120, slotName: 'signerName'},
  {title: '签字内容', dataIndex: 'signatureContent', width: 200, slotName: 'signatureContent'},
  {title: '签字评分', dataIndex: 'signatureEvaluation', width: 120, slotName: 'signatureEvaluation'},
  {title: '评价详情', dataIndex: 'evaluationDetail', width: 200, slotName: 'evaluationDetail'},
  {title: '操作', dataIndex: 'actions', width: 100, slotName: 'actions'}
])

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal && props.workOrderId) {
    isEditMode.value = true
    initData()
  } else if (newVal) {
    isEditMode.value = false
    resetData()
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 区域选择变化处理
const handleRegionChange = (record, value, option) => {
  if (option) {
    record.regionName = option.name || ''
  } else {
    record.regionName = ''
  }
}

// 标靶虫害选择变化处理
const handlePestTypeChange = (record, value) => {
  if (value) {
    const selectedPestType = pestTypeOptions.value.find(item => item.id === value)
    if (selectedPestType) {
      record.pestTypeName = selectedPestType.name
    }
  } else {
    record.pestTypeName = ''
  }
  record.materialId = ''
  handleMaterialChange(record, '')
}

const filterMaterialOptions = (pestTypeId) => {
  var mps = []
  if (pestTypeId) {
    mps = materialOptions.value.filter(item => item.categoryId === pestTypeId)
  } else {
    mps = [...materialOptions.value]
  }
  return mps;
}

// 物料选择变化处理
const handleMaterialChange = (record, value) => {
  if (value) {
    const selectedMaterial = materialOptions.value.find(item => item.id === value)
    if (selectedMaterial) {
      record.materialName = selectedMaterial.name
      record.dilutionRatio = selectedMaterial.dilutionRatio || ''
      record.unit = selectedMaterial.unit || ''
      record.usageMethod = selectedMaterial.usageMethod || ''
    }
  } else {
    record.materialName = ''
    record.dilutionRatio = ''
    record.unit = ''
    record.usageMethod = ''
  }
}

// 风险类型变更
const handleCaseDescriptionChange = (record, values, option) => {
  console.log('value', record, values, option,)
  if (values && values.length) {
    let names = values.map(item => caseDescriptionMap.value.get(item))
    record.caseDescriptionNames = names.join() || ''
  } else {
    record.caseDescriptionNames = ''
  }
}
// 建议措施选择变化处理
const handleSuggestionMeasureChange = (record, value, option) => {
  if (option) {
    record.suggestionMeasureName = option.name || ''
  } else {
    record.suggestionMeasureName = ''
  }
}

// 门店类型变化处理（用于级联筛选区域）
const handleStoreTypeChange = (record, value) => {
  // 根据选择的门店类型筛选区域选项
  if (value) {
    filteredRegionOptions.value = regionOptions.value.filter(region => region.storeTypeId === value)
  } else {
    filteredRegionOptions.value = [...regionOptions.value]
  }

  // 清除已选择的区域，因为选项可能已变化
  record.regionId = ''
  record.regionName = ''
}

// 设备位置选择变化处理
const handleDeviceLocationChange = (record, value, option) => {
  if (option) {
    record.deviceLocationName = option.name
  } else {
    record.deviceLocationName = ''
  }
}

// 设备类型选择变化处理
const handleDeviceTypeChange = (record, value, option) => {
  if (option) {
    record.deviceTypeName = option.name
  } else {
    record.deviceTypeName = ''
  }
}

// 设备状况选择变化处理
const handleDeviceConditionChange = (record, value, option) => {
  if (option) {
    record.deviceConditionName = option.name
  } else {
    record.deviceConditionName = ''
  }
}

// 集团定制配置选择变化处理
const handleStoreCustomConfigChange = (record, value, option) => {
  console.log('value', record, value, option)
  if (value) {
    const selectedConfig = storeCustomConfigOptions.value.find(item => item.id === value)
    if (selectedConfig) {
      record.optionLabel = selectedConfig.optionLabel
      record.optionName = selectedConfig.optionName
      record.optionType = selectedConfig.optionType
      record.optionValues = selectedConfig.optionValues
      record.isRequired = selectedConfig.isRequired
      record.remark = selectedConfig.remark
    }
  } else {
    record.optionLabel = ''
    record.optionName = ''
    record.optionType = ''
    record.optionValues = ''
    record.isRequired = false
    record.remark = ''
  }
  // 清空值
  record.storeCustomValue = ''
}

// 初始化数据
const initData = async () => {
  if (!props.workOrderId) return

  loading.value = true
  try {
    // 并行获取各个子表的数据
    const [actionItemRes, pestSurveyRes, deviceDetailRes, materialSummaryRes, checkinRecordRes, extensionInfoRes, storeCustomConfigRes, signatureRecordRes] = await Promise.all([
      getWorkOrderActionItemList({workorderId: props.workOrderId}),
      getWorkOrderPestSurveyList({workorderId: props.workOrderId}),
      getWorkOrderDeviceDetailList({workorderId: props.workOrderId}),
      getWorkOrderMaterialSummaryList({workorderId: props.workOrderId}),
      getWorkOrderCheckinRecordList({workorderId: props.workOrderId}),
      getWorkOrderExtensionInfoList({workorderId: props.workOrderId}), // 添加集团定制信息查询
      getStoreCustomConfigList(props.workOrderId), // 获取门店定制配置列表
      getWorkOrderSignatureRecordList({workorderId: props.workOrderId}) // 获取签字记录信息
    ])

    // 更新门店定制配置选项
    if (storeCustomConfigRes.code === 0 && storeCustomConfigRes.data) {
      console.log('storeCustomConfigRes.data', storeCustomConfigRes.data)
      storeCustomConfigOptions.value = storeCustomConfigRes.data.map(item => ({
        storeCustomConfigId: item.storeCustomConfigId,
        remark: item.remark,
        optionLabel: item.optionLabel,
        optionName: item.optionName,
        optionType: item.optionType,
        optionValues: item.optionValues.join() ,
        isRequired: item.isRequired
      }))
    }

    // 初始化行动项目
    if (actionItemRes.code === 0 && actionItemRes.data) {
      actionItems.value = actionItemRes.data.map(item => ({
        ...item,
        id: item.id,
        completed: item.completed === true || item.completed === 1 || item.completed === 'true',
        // 将逗号分隔的ID字符串转为数组，用于多选下拉框
        caseDescriptionIdsArray: item.caseDescriptionIds ? item.caseDescriptionIds.split(',').filter(id => id) : [],
        // 确保文本字段有默认值
        regionText: item.regionText || item.regionName || '',
        caseDescriptionText: item.caseDescriptionText || '',
        suggestionMeasureText: item.suggestionMeasureText || '',
        // 确保包含照片记录字段
        photoRecords: item.photoRecords || ''
      }))
    } else {
      actionItems.value = []
    }

    // 初始化虫情勘察
    if (pestSurveyRes.code === 0 && pestSurveyRes.data) {
      pestSurveys.value = pestSurveyRes.data.map(item => ({
        ...item,
        id: item.id,
        // 确保文本字段有默认值
        regionName: item.regionName || ''
      }))
    } else {
      pestSurveys.value = []
    }

    // 初始化设备详情
    if (deviceDetailRes.code === 0 && deviceDetailRes.data) {
      deviceDetails.value = deviceDetailRes.data.map(item => ({
        ...item,
        id: item.id,
        captured: item.captured === true || item.captured === 1 || item.captured === 'true',
        materialReplaced: item.materialReplaced === true || item.materialReplaced === 1 || item.materialReplaced === 'true',
        // 确保文本字段有默认值
        regionName: item.regionName || '',
        deviceLocationName: item.deviceLocationName || '',
        deviceTypeName: item.deviceTypeName || '',
        deviceConditionName: item.deviceConditionName || '',
        // 确保包含照片记录字段
        photoRecords: item.photoRecords || ''
      }))
    } else {
      deviceDetails.value = []
    }

    // 初始化物料汇总
    if (materialSummaryRes.code === 0 && materialSummaryRes.data) {
      materialSummaries.value = materialSummaryRes.data.map(item => ({
        ...item,
        id: item.id,
        // 确保文本字段有默认值
        regionName: item.regionName || '',
        pestTypeName: item.pestTypeName || '',
        materialName: item.materialName || ''
      }))
    } else {
      materialSummaries.value = []
    }

    // 初始化签到记录
    if (checkinRecordRes.code === 0 && checkinRecordRes.data) {
      checkinRecords.value = checkinRecordRes.data.map(item => ({
        ...item,
        id: item.id,
        // 将毫秒时间戳转换为日期对象
        checkinTime: item.checkinTime ? new Date(item.checkinTime) : null,
        checkinLongitude: item.checkinLongitude || null,
        checkinLatitude: item.checkinLatitude || null,
        checkinAddress: item.checkinAddress || '',
        invalidStatus: item.invalidStatus !== null && item.invalidStatus !== undefined ? item.invalidStatus : 0,
        workStartTime: item.workStartTime ? new Date(item.workStartTime) : null,
        workSubmitTime: item.workSubmitTime ? new Date(item.workSubmitTime) : null
      }))
    } else {
      checkinRecords.value = []
    }

    // 初始化集团定制项
    if (extensionInfoRes.code === 0 && extensionInfoRes.data) {
      groupCustomItems.value = extensionInfoRes.data.map(item => ({
        ...item,
        id: item.id
      }))
    } else {
      groupCustomItems.value = []
    }

    // 初始化签字记录
    if (signatureRecordRes.code === 0 && signatureRecordRes.data) {
      signatureRecords.value = signatureRecordRes.data.map(item => ({
        ...item,
        id: item.id,
        // 将毫秒时间戳转换为日期对象
        signatureTime: item.signatureTime ? new Date(item.signatureTime) : null
      }))
    } else {
      signatureRecords.value = []
    }
  } catch (error) {
    Message.error('获取工单子表信息失败')
    console.error('获取工单子表信息失败:', error)
    resetData()
  } finally {
    loading.value = false
  }
}

// 重置数据
const resetData = () => {
  actionItems.value = []
  pestSurveys.value = []
  deviceDetails.value = []
  materialSummaries.value = []
  checkinRecords.value = [] // 重置签到记录
  groupCustomItems.value = [] // 重置集团定制项
}

// 添加行动项目
const addActionItem = () => {
  actionItems.value.push({
    id: '',
    caseTypeId: '',
    caseTypeName: '',
    regionId: '',
    regionName: '',
    regionText: '',
    caseDescriptionIds: '',
    caseDescriptionNames: '',
    caseDescriptionText: '',
    caseDescriptionIdsArray: [],
    suggestionMeasureId: '',
    suggestionMeasureName: '',
    suggestionMeasureText: '',
    followerName: '',
    followUpCount: 0,
    completed: false,
    storeTypeId: '', // 添加门店类型字段
    storeTypeName: '', // 添加门店类型名称字段
    photoRecords: '' // 添加照片记录字段
  })
}

// 删除行动项目
const deleteActionItem = (record) => {
  // 对于有ID的记录，标记为待删除状态
  if (record.id && !record.id.startsWith('n')) {
    record._toDelete = true
  } else {
    // 对于无ID的记录，直接从列表中移除
    actionItems.value = actionItems.value.filter(item => item !== record)
  }
}

// 添加虫情勘察
const addPestSurvey = () => {
  pestSurveys.value.push({
    id: '',
    storeTypeId: '',
    regionId: '',
    regionName: '',
    mouseCount: 0,
    hasMouseTrace: false,
    mouseDetails: '',
    cockroachCount: 0,
    cockroachDetails: '',
    flyingInsectCount: 0,
    remark: ''
  })
}

// 删除虫情勘察
const deletePestSurvey = (record) => {
  // 对于有ID的记录，标记为待删除状态
  if (record.id && !record.id.startsWith('n')) {
    record._toDelete = true
  } else {
    // 对于无ID的记录，直接从列表中移除
    pestSurveys.value = pestSurveys.value.filter(item => item !== record)
  }
}

// 添加设备详情
const addDeviceDetail = () => {
  deviceDetails.value.push({
    id: '',
    storeTypeId: '',
    regionId: '',
    regionName: '',
    deviceLocationId: '',
    deviceLocationName: '',
    deviceLocationText: '',
    deviceTypeId: '',
    deviceTypeName: '',
    deviceConditionId: '',
    deviceConditionName: '',
    captured: false,
    materialReplaced: false,
    captureCount: 0,
    remark: '',
    photoRecords: '' // 添加照片记录字段
  })
}

// 删除设备详情
const deleteDeviceDetail = (record) => {
  // 对于有ID的记录，标记为待删除状态
  if (record.id && !record.id.startsWith('n')) {
    record._toDelete = true
  } else {
    // 对于无ID的记录，直接从列表中移除
    deviceDetails.value = deviceDetails.value.filter(item => item !== record)
  }
}

// 添加物料汇总
const addMaterialSummary = () => {
  materialSummaries.value.push({
    id: '',
    storeTypeId: '',
    regionId: '',
    regionName: '',
    pestTypeId: '',
    pestTypeName: '',
    materialId: '',
    materialName: '',
    dilutionRatio: '',
    unit: '',
    materialQuantity: 0,
    usageMethod: '',
    remark: ''
  })
}

// 删除物料汇总
const deleteMaterialSummary = (record) => {
  // 对于有ID的记录，标记为待删除状态
  if (record.id && !record.id.startsWith('n')) {
    record._toDelete = true
  } else {
    // 对于无ID的记录，直接从列表中移除
    materialSummaries.value = materialSummaries.value.filter(item => item !== record)
  }
}

// 添加签到记录
const addCheckinRecord = () => {
  checkinRecords.value.push({
    id: '',
    checkinTime: null,
    checkinLongitude: null,
    checkinLatitude: null,
    checkinAddress: '',
    invalidStatus: 0, // 默认为未校验状态
    workStartTime: null,
    workSubmitTime: null
  })
}

// 删除签到记录
const deleteCheckinRecord = (record) => {
  // 对于有ID的记录，标记为待删除状态
  if (record.id && !record.id.startsWith('n')) {
    record._toDelete = true
  } else {
    // 对于无ID的记录，直接从列表中移除
    checkinRecords.value = checkinRecords.value.filter(item => item !== record)
  }
}

// 添加集团定制项
const addGroupCustomItem = () => {
  groupCustomItems.value.push({
    id: '',
    storeCustomConfigId: '',
    optionType: '',
    remark: '',
    optionValues: '',
    storeCustomValue: '',
  })
}

// 删除集团定制项
const deleteGroupCustomItem = (record) => {
  // 对于有ID的记录，标记为待删除状态
  if (record.id && !record.id.startsWith('n')) {
    record._toDelete = true
  } else {
    // 对于无ID的记录，直接从列表中移除
    groupCustomItems.value = groupCustomItems.value.filter(item => item !== record)
  }
}

// 恢复删除行动项目
const restoreActionItem = (record) => {
  record._toDelete = false
}

// 恢复删除虫情勘察
const restorePestSurvey = (record) => {
  record._toDelete = false
}

// 恢复删除设备详情
const restoreDeviceDetail = (record) => {
  record._toDelete = false
}

// 恢复删除物料汇总
const restoreMaterialSummary = (record) => {
  record._toDelete = false
}

// 恢复删除签到记录
const restoreCheckinRecord = (record) => {
  record._toDelete = false
}

// 恢复删除集团定制项
const restoreGroupCustomItem = (record) => {
  record._toDelete = false
}

// 添加签字记录
const addSignatureRecord = () => {
  signatureRecords.value.push({
    id: '',
    workorderId: props.workOrderId,
    signerType: '',
    signerName: '',
    signatureTime: null,
    signatureContent: '',
    signatureEvaluation: null,
    evaluationDetail: ''
  })
}

// 删除签字记录
const deleteSignatureRecord = (record) => {
  // 对于有ID的记录，标记为待删除状态
  if (record.id && !record.id.startsWith('n')) {
    record._toDelete = true
  } else {
    // 对于无ID的记录，直接从列表中移除
    signatureRecords.value = signatureRecords.value.filter(item => item !== record)
  }
}

// 恢复签字记录
const restoreSignatureRecord = (record) => {
  record._toDelete = false
}

// 处理确认按钮
const handleOk = async (done) => {
  try {
    loading.value = true

    // 过滤掉标记为待删除的记录
    const actionItemsToSave = actionItems.value.filter(item => !item._toDelete)
    const pestSurveysToSave = pestSurveys.value.filter(item => !item._toDelete)
    const deviceDetailsToSave = deviceDetails.value.filter(item => !item._toDelete)
    const materialSummariesToSave = materialSummaries.value.filter(item => !item._toDelete)
    const checkinRecordsToSave = checkinRecords.value.filter(item => !item._toDelete) // 添加签到记录过滤
    const groupCustomItemsToSave = groupCustomItems.value.filter(item => !item._toDelete) // 添加集团定制项过滤
    const signatureRecordsToSave = signatureRecords.value.filter(item => !item._toDelete) // 添加签字记录过滤

    // 收集待删除的记录ID
    const actionItemsToDelete = actionItems.value.filter(item => item._toDelete && item.id && !item.id.startsWith('n'))
    const pestSurveysToDelete = pestSurveys.value.filter(item => item._toDelete && item.id && !item.id.startsWith('n'))
    const deviceDetailsToDelete = deviceDetails.value.filter(item => item._toDelete && item.id && !item.id.startsWith('n'))
    const materialSummariesToDelete = materialSummaries.value.filter(item => item._toDelete && item.id && !item.id.startsWith('n'))
    const checkinRecordsToDelete = checkinRecords.value.filter(item => item._toDelete && item.id && !item.id.startsWith('n')) // 添加签到记录删除收集
    const groupCustomItemsToDelete = groupCustomItems.value.filter(item => item._toDelete && item.id && !item.id.startsWith('n')) // 添加集团定制项删除收集
    const signatureRecordsToDelete = signatureRecords.value.filter(item => item._toDelete && item.id && !item.id.startsWith('n')) // 添加签字记录删除收集

    // 处理行动项目数据，将数组转为逗号分隔的字符串
    const processedActionItems = actionItemsToSave.map(item => {
      return {
        ...item,
        caseDescriptionIds: item.caseDescriptionIdsArray ? item.caseDescriptionIdsArray.join(',') : '',
        // 同步选择的ID和文本
        caseTypeName: caseTypeOptions.value.find(opt => opt.id === item.caseTypeId)?.name || '',
        regionName: regionOptions.value.find(opt => opt.id === item.regionId)?.name || '',
        suggestionMeasureName: suggestionMeasureOptions.value.find(opt => opt.id === item.suggestionMeasureId)?.name || ''
      }
    })

    // 准备提交数据
    const submitData = {
      workOrderId: props.workOrderId,
      actionItems: processedActionItems,
      pestSurveys: pestSurveysToSave,
      deviceDetails: deviceDetailsToSave,
      materialSummaries: materialSummariesToSave,
      checkinRecords: checkinRecordsToSave, // 添加签到记录到提交数据
      groupCustomItems: groupCustomItemsToSave, // 添加集团定制项到提交数据
      signatureRecords: signatureRecordsToSave // 添加签字记录到提交数据
    }

    // 保存行动项目
    const actionItemPromises = processedActionItems.map(item => {
      const data = {...item, workorderId: props.workOrderId};
      // 删除前端生成的id和临时字段
      delete data.id;
      delete data.caseDescriptionIdsArray;
      // 如果有ID且不是nanoid生成的，则认为是数据库记录，进行更新
      if (item.id && !item.id.startsWith('n')) {
        // 更新已存在的记录
        data.id = item.id;
        return updateWorkOrderActionItem(data);
      } else {
        // 创建新记录
        return addWorkOrderActionItem(data);
      }
    });

    // 保存虫情勘察
    const pestSurveyPromises = pestSurveysToSave.map(item => {
      const data = {
        ...item,
        workorderId: props.workOrderId,
        // 同步选择的ID和文本
        regionName: regionOptions.value.find(opt => opt.id === item.regionId)?.name || item.regionName || ''
      };
      delete data.id;
      // 如果有ID且不是nanoid生成的，则认为是数据库记录，进行更新
      if (item.id && !item.id.startsWith('n')) {
        // 更新已存在的记录
        data.id = item.id;
        return updateWorkOrderPestSurvey(data);
      } else {
        // 创建新记录
        return addWorkOrderPestSurvey(data);
      }
    });

    // 保存设备详情
    const deviceDetailPromises = deviceDetailsToSave.map(item => {
      const data = {
        ...item,
        workorderId: props.workOrderId,
        // 同步选择的ID和文本
        regionName: regionOptions.value.find(opt => opt.id === item.regionId)?.name || item.regionName || '',
        deviceLocationName: deviceLocationOptions.value.find(opt => opt.id === item.deviceLocationId)?.name || item.deviceLocationName || '',
        deviceTypeName: deviceTypeOptions.value.find(opt => opt.id === item.deviceTypeId)?.name || item.deviceTypeName || '',
        deviceConditionName: deviceConditionOptions.value.find(opt => opt.id === item.deviceConditionId)?.name || item.deviceConditionName || ''
      };
      delete data.id;
      // 如果有ID且不是nanoid生成的，则认为是数据库记录，进行更新
      if (item.id && !item.id.startsWith('n')) {
        // 更新已存在的记录
        data.id = item.id;
        return updateWorkOrderDeviceDetail(data);
      } else {
        // 创建新记录
        return addWorkOrderDeviceDetail(data);
      }
    });

    // 保存物料汇总
    const materialSummaryPromises = materialSummariesToSave.map(item => {
      const data = {
        ...item,
        workorderId: props.workOrderId,
        // 同步选择的ID和文本
        regionName: regionOptions.value.find(opt => opt.id === item.regionId)?.name || item.regionName || '',
        pestTypeName: pestTypeOptions.value.find(opt => opt.id === item.pestTypeId)?.name || item.pestTypeName || '',
        materialName: materialOptions.value.find(opt => opt.id === item.materialId)?.name || item.materialName || ''
      };
      delete data.id;
      // 如果有ID且不是nanoid生成的，则认为是数据库记录，进行更新
      if (item.id && !item.id.startsWith('n')) {
        // 更新已存在的记录
        data.id = item.id;
        return updateWorkOrderMaterialSummary(data);
      } else {
        // 创建新记录
        return addWorkOrderMaterialSummary(data);
      }
    });

    // 保存签到记录
    const checkinRecordPromises = checkinRecordsToSave.map(item => {
      const data = {
        ...item,
        workorderId: props.workOrderId,
        // 将日期对象转换为毫秒时间戳
        checkinTime: item.checkinTime ? new Date(item.checkinTime).getTime() : null,
        workStartTime: item.workStartTime ? new Date(item.workStartTime).getTime() : null,
        workSubmitTime: item.workSubmitTime ? new Date(item.workSubmitTime).getTime() : null
      };
      delete data.id;
      // 如果有ID且不是nanoid生成的，则认为是数据库记录，进行更新
      if (item.id && !item.id.startsWith('n')) {
        // 更新已存在的记录
        data.id = item.id;
        return updateWorkOrderCheckinRecord(data);
      } else {
        // 创建新记录
        return addWorkOrderCheckinRecord(data);
      }
    });

    // 保存集团定制信息
    const groupCustomPromises = groupCustomItemsToSave.map(item => {
      const data = {
        ...item,
        workorderId: props.workOrderId
      };
      delete data.id;
      // 如果有ID且不是nanoid生成的，则认为是数据库记录，进行更新
      if (item.id && !item.id.startsWith('n')) {
        // 更新已存在的记录
        data.id = item.id;
        return updateWorkOrderExtensionInfo(data);
      } else {
        // 创建新记录
        return addWorkOrderExtensionInfo(data);
      }
    });

    // 保存签字记录
    const signatureRecordPromises = signatureRecordsToSave.map(item => {
      const data = {
        ...item,
        workorderId: props.workOrderId,
        // 将日期对象转换为毫秒时间戳
        signatureTime: item.signatureTime ? new Date(item.signatureTime).getTime() : null
      };
      delete data.id;
      // 如果有ID且不是nanoid生成的，则认为是数据库记录，进行更新
      if (item.id && !item.id.startsWith('n')) {
        // 更新已存在的记录
        data.id = item.id;
        return updateWorkOrderSignatureRecord(data);
      } else {
        // 创建新记录
        return addWorkOrderSignatureRecord(data);
      }
    });

    // 删除操作的Promise
    const deletePromises = [
      ...actionItemsToDelete.map(item => deleteWorkOrderActionItem(item.id)),
      ...pestSurveysToDelete.map(item => deleteWorkOrderPestSurvey(item.id)),
      ...deviceDetailsToDelete.map(item => deleteWorkOrderDeviceDetail(item.id)),
      ...materialSummariesToDelete.map(item => deleteWorkOrderMaterialSummary(item.id)),
      ...checkinRecordsToDelete.map(item => deleteWorkOrderCheckinRecord(item.id)),
      ...groupCustomItemsToDelete.map(item => deleteWorkOrderExtensionInfo(item.id)),
      ...signatureRecordsToDelete.map(item => deleteWorkOrderSignatureRecord(item.id))
    ];

    // 并行执行所有保存和删除操作
    await Promise.all([
      ...actionItemPromises,
      ...pestSurveyPromises,
      ...deviceDetailPromises,
      ...materialSummaryPromises,
      ...checkinRecordPromises,
      ...groupCustomPromises,
      ...deletePromises
    ]);

    Message.success('保存成功')
    emit('success', submitData)
    visible.value = false
    done(true)
    return true
  } catch (error) {
    Message.error('保存失败: ' + error.message)
    console.error('保存失败:', error)
    done(false)
    return false
  } finally {
    loading.value = false
  }
}

// 处理取消按钮
const handleCancel = () => {
  visible.value = false
}

// 添加获取照片文件列表的方法
const getPhotoFileList = (record,urlsKey) => {
  if (!record[urlsKey]) return []

  // 如果photoRecords是字符串（逗号分隔的URL）
  if (typeof record[urlsKey] === 'string') {
    return record[urlsKey].split(',').filter(url => url.trim()).map((url, index) => ({
      uid: `${record.id || 'new'}-${index}`,
      name: `photo-${index + 1}.jpg`,
      url: url.trim(),
      status: 'done'
    }))
  }

  // 如果photoRecords已经是文件列表格式
  if (Array.isArray(record[urlsKey])) {
    return record[urlsKey]
  }

  return []
}

// 添加获取集团定制字段照片文件列表的方法
const getPhotoFileListForCustomField = (record) => {
  if (!record.storeCustomValue) return []

  // 如果storeCustomValue是字符串（逗号分隔的URL）
  if (typeof record.storeCustomValue === 'string') {
    return record.storeCustomValue.split(',').filter(url => url.trim()).map((url, index) => ({
      uid: `${record.id || 'new'}-${index}`,
      name: `photo-${index + 1}.jpg`,
      url: url.trim(),
      status: 'done'
    }))
  }

  // 如果storeCustomValue已经是文件列表格式
  if (Array.isArray(record.storeCustomValue)) {
    return record.storeCustomValue
  }

  return []
}

// 添加处理照片上传的方法
const handlePhotoUpload = async (options, record,urlsKey) => {
  const {onProgress, onError, onSuccess, fileItem, name} = options

  console.log('上传文件:', options)
  try {
    // 模拟上传进度
    // onProgress({ percent: 30 })
    // 调用实际的上传API
    const response = await uploadFile(fileItem.file)

    // onProgress({ percent: 90 })
    // onProgress({ percent: 100 })

    // 检查响应是否成功并获取文件路径
    if (response && response.filePath) {
      const filePath = response.filePath
      console.log('上传成功:', filePath)

      // 更新记录中的照片URL
      if (typeof record[urlsKey] === 'string') {
        // 如果已有照片记录，则添加新的URL
        if (record[urlsKey]) {
          record[urlsKey] = record[urlsKey] + ',' + filePath
        } else {
          // 如果没有照片记录，则直接赋值
          record[urlsKey] = filePath
        }
      } else {
        // 如果photoRecords不是字符串，则初始化为空字符串并添加新URL
        record[urlsKey] = filePath
      }

      // 构造文件对象供上传组件使用
      const fileObj = {
        uid: fileItem.uid,
        name: fileItem.name,
        url: filePath,
        status: 'done'
      }

      onSuccess(fileObj)
    } else {
      throw new Error('上传失败，未获取到文件路径')
    }
  } catch (error) {
    console.error('文件上传失败:', error)
    onError(error)
  }
}

// 添加处理集团定制字段照片上传的方法
const handlePhotoUploadForCustomField = async (options, record) => {
  const {onProgress, onError, onSuccess, fileItem, name} = options

  console.log('上传文件:', options)
  try {
    // 模拟上传进度
    // onProgress({ percent: 30 })
    // 调用实际的上传API
    const response = await uploadFile(fileItem.file)

    // onProgress({ percent: 90 })
    // onProgress({ percent: 100 })

    // 检查响应是否成功并获取文件路径
    if (response && response.filePath) {
      const filePath = response.filePath
      console.log('上传成功:', filePath)

      // 更新记录中的照片URL
      if (typeof record.storeCustomValue === 'string') {
        // 如果已有照片记录，则添加新的URL
        if (record.storeCustomValue) {
          record.storeCustomValue = record.storeCustomValue + ',' + filePath
        } else {
          // 如果没有照片记录，则直接赋值
          record.storeCustomValue = filePath
        }
      } else {
        // 如果storeCustomValue不是字符串，则初始化为空字符串并添加新URL
        record.storeCustomValue = filePath
      }

      // 构造文件对象供上传组件使用
      const fileObj = {
        uid: fileItem.uid,
        name: fileItem.name,
        url: filePath,
        status: 'done'
      }

      onSuccess(fileObj)
    } else {
      throw new Error('上传失败，未获取到文件路径')
    }
  } catch (error) {
    console.error('文件上传失败:', error)
    onError(error)
  }
}

// 添加处理照片变更的方法
const handlePhotoChange = (fileList, record, urlsKey) => {
  console.debug('handlePhotoChange 照片变更:', JSON.stringify(fileList,null,2))
  // 更新记录中的照片列表
  // record[urlsKey] = fileList.filter(d => d.status === 'done').map(file => file.url || '').filter(url => url).join(',')

  console.debug('handlePhotoChange 照片变更:', JSON.stringify(fileList,null,2))
  // 更新记录中的照片列表，只保留已上传成功的文件URL
  record[urlsKey] = fileList
      .filter(d => d.status === 'done')
      .map(file => {
        // 如果file.url已经是一个完整的URL(包含http)，直接使用
        // 否则返回file.response.filePath(来自服务器的响应)
        if (file.url && (file.url.startsWith('http') || file.url.startsWith('https'))) {
          return file.url
        } else if (file.response && file.response.filePath) {
          return file.response.filePath
        }else if (file.response && file.response.url) {
          return file.response.url
        }
        return ''
      })
      .filter(url => url)
      .join(',')
}

// 添加处理集团定制字段照片变更的方法
const handlePhotoChangeForCustomField = (fileList, record) => {
  console.debug('handlePhotoChangeForCustomField 照片变更:', JSON.stringify(fileList,null,2))
  // 更新记录中的照片列表，只保留已上传成功的文件URL
  record.storeCustomValue = fileList
    .filter(d => d.status === 'done')
    .map(file => {
      // 如果file.url已经是一个完整的URL(包含http)，直接使用
      // 否则返回file.response.filePath(来自服务器的响应)
      if (file.url && (file.url.startsWith('http') || file.url.startsWith('https'))) {
        return file.url
      } else if (file.response && file.response.filePath) {
        return file.response.filePath
      }else if (file.response && file.response.url) {
        return file.response.url
      }
      return ''
    })
    .filter(url => url)
    .join(',')

}

// 添加获取签字文件列表的方法
const getSignatureFileList = (record) => {
  if (!record.signatureContent) return []

  // 如果signatureContent是字符串（逗号分隔的URL）
  if (typeof record.signatureContent === 'string') {
    return record.signatureContent.split(',').filter(url => url.trim()).map((url, index) => ({
      uid: `${record.id || 'new'}-${index}`,
      name: `signature-${index + 1}.jpg`,
      url: url.trim(),
      status: 'done'
    }))
  }

  // 如果signatureContent已经是文件列表格式
  if (Array.isArray(record.signatureContent)) {
    return record.signatureContent
  }

  return []
}
// 获取签字人类型文本
const getSignerTypeText = (signerType) => {
  const signerTypeMap = {
    'employee': '技术人员',
    'customer': '客户'
  };
  return signerTypeMap[signerType] || signerType || '—';
}
// 添加处理签字上传的方法
const handleSignatureUpload = async (options, record) => {
  const {onProgress, onError, onSuccess, fileItem, name} = options

  console.log('上传签字:', options)
  try {
    // 调用实际的上传API
    const response = await uploadFile(fileItem.file)

    // 检查响应是否成功并获取文件路径
    if (response && response.filePath) {
      const filePath = response.filePath
      console.log('签字上传成功:', filePath)

      // 更新记录中的签字URL
      if (typeof record.signatureContent === 'string') {
        // 如果已有签字记录，则添加新的URL
        if (record.signatureContent) {
          record.signatureContent = record.signatureContent + ',' + filePath
        } else {
          // 如果没有签字记录，则直接赋值
          record.signatureContent = filePath
        }
      } else {
        // 如果signatureContent不是字符串，则初始化为空字符串并添加新URL
        record.signatureContent = filePath
      }

      // 构造文件对象供上传组件使用
      const fileObj = {
        uid: fileItem.uid,
        name: fileItem.name,
        url: filePath,
        status: 'done'
      }

      onSuccess(fileObj)
    } else {
      throw new Error('上传失败，未获取到文件路径')
    }
  } catch (error) {
    console.error('签字文件上传失败:', error)
    onError(error)
  }
}

// 添加处理签字变更的方法
const handleSignatureChange = (fileList, record) => {
  console.debug('handleSignatureChange 签字变更:', JSON.stringify(fileList,null,2))
  // 更新记录中的签字列表，只保留服务器返回的永久URL，过滤掉临时的Blob URL
  record.signatureContent = fileList
    .filter(d => d.status === 'done')
    .map(file => {
      // 优先使用服务器返回的永久URL
      if (file.response && file.response.filePath) {
        return file.response.filePath
      } else if (file.url && !file.url.startsWith('blob:')) {
        // 如果url不是blob URL，则使用它
        return file.url
      }
      // 忽略临时的blob URL
      return null
    })
    .filter(url => url) // 过滤掉null值
    .join(',')
}

// 初始化基础数据
const initBaseData = async () => {

  try {
    // 获取所有基础数据
    const [baseDataRes, userRes] = await Promise.all([
      getInitBaseData(),
      getAllUser() // 获取所有用户
    ]);

    if (baseDataRes.code === 0 && baseDataRes.data) {
      const baseData = baseDataRes.data

      // 处理情况类型选项
      if (baseData.caseTypeList) {
        caseTypeOptions.value = baseData.caseTypeList.map(item => ({
          id: item.id,
          name: item.caseTypeName
        }))
      }

      // 处理区域选项
      if (baseData.regionManagementList) {
        regionOptions.value = baseData.regionManagementList.map(item => ({
          id: item.id,
          name: item.regionName,
          storeTypeId: item.storeTypeId
        }))
        // 默认显示所有区域选项
        filteredRegionOptions.value = [...regionOptions.value]
      }

      // 处理门店类型选项
      if (baseData.storeTypeList) {
        storeTypeOptions.value = baseData.storeTypeList.map(item => ({
          id: item.id,
          name: item.typeName
        }))
      }
      cascaderRegionOptions.value = baseData.storeTypeList.map(item => ({
        value: item.id,
        label: item.typeName,
        children: item.regionManagementList.map(regionItem => ({
          value: regionItem.id,
          label: regionItem.regionName
        }))
      }))

      // 处理风险类型选项
      if (baseData.caseDescriptionList) {
        caseDescriptionOptions.value = baseData.caseDescriptionList.map(item => ({
          id: item.id,
          name: item.caseDescName
        }))
        caseDescriptionOptions.value.forEach(item => {
          caseDescriptionMap.value.set(item.id, item.name)
        })
      }

      // 处理建议措施选项
      if (baseData.suggestionMeasureList) {
        suggestionMeasureOptions.value = baseData.suggestionMeasureList.map(item => ({
          id: item.id,
          name: item.measureName
        }))
      }

      // 处理设备位置选项
      if (baseData.deviceLocationList) {
        deviceLocationOptions.value = baseData.deviceLocationList.map(item => ({
          id: item.id,
          name: item.locationName
        }))
      }

      // 处理设备类型选项
      if (baseData.deviceTypeList) {
        deviceTypeOptions.value = baseData.deviceTypeList.map(item => ({
          id: item.id,
          name: item.deviceTypeName
        }))
      }

      // 处理设备状况选项
      if (baseData.deviceConditionList) {
        deviceConditionOptions.value = baseData.deviceConditionList.map(item => ({
          id: item.id,
          name: item.conditionName
        }))
      }

      // 处理标靶虫害选项
      if (baseData.pestTypeList) {
        pestTypeOptions.value = baseData.pestTypeList.map(item => ({
          id: item.id,
          name: item.pestName
        }))
      }

      // 处理物料选项
      if (baseData.materialInfoList) {
        materialOptions.value = baseData.materialInfoList.map(item => ({
          id: item.id,
          categoryId: item.categoryId,
          name: item.materialName,
          unit: item.unit,
          dilutionRatio: item.dilutionRatio,
          usageMethod: item.usageMethod
        }))
      }
    }
    
    // 处理用户选项
    if (userRes && userRes.code === 0 && userRes.data) {
      userOptions.value = userRes.data.map(user => ({
        id: user.id,
        userName: user.nickname
      }));
    }
  } catch (error) {
    console.error('获取基础数据失败:', error)
  }
}

// 获取行样式
const getRowClass = (record) => {
  return record._toDelete ? 'row-to-delete' : ''
}

// 组件挂载时获取基础数据
onMounted(async () => {
  await initBaseData()
})

</script>

<style scoped>
.row-to-delete :deep(.arco-input-disabled) {
  color: #999;
  cursor: not-allowed;
}

.upload-button-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #4e5969;
  font-size: 14px;
}
</style>